import { useState, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { HeaderContainer, ContainerHiddenSubHeader, Logo, GridLogin, GridMenuHiddenUp, GridMenuHiddenDown, HeaderContainerNotScrolled } from "./template";
import DropDownLanguages from "../DropDownLanguages";
import { BLACK, BLUE_MAIN, GREEN_MAIN, WHITE } from "../../theme/colors";
import { Grid, Box, Avatar, IconButton, styled, useStepContext } from "@mui/material";
import settings from "../../utilities/settings";
import LoginButton from "../LoginButton";
import RegisterButton from "../RegisterButton";
import { useAuth } from "react-oidc-context";
import { IDrawerItem } from "../../hooks/drawer/models";
import { useUserContext } from "../../hooks/auth/UserProvider";
import { isNationalEntityUser, adminAreaRoles, Roles } from "../../helpers/authorizedUser";
import { useDrawer } from "../../hooks/drawer/DrawerProvider";
import MenuIcon from '@mui/icons-material/Menu';
import AppsIcon from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import { BLUE_LIGHT_RGB_BORDER } from "../../theme/colors";
import { useNavigate } from "react-router-dom";
import UserFidelityLevelHeader from "./userFidelityLevelHeader";
import { useFidelityLevel } from "../../hooks/auth/FidelityLevel";
import DynamicMenu from "../MenuItems";

export type MenuItemType = {
  nome: string;
  link?: string;
  submenu?: MenuItemType[];
};

export interface MenuDrawerItemSideBar {
  nome: string;
  link?: string;
  submenu?: MenuDrawerItemSideBar[];
  icon?: ReactNode;
}

const Header = () =>{
    const { t, i18n } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const auth = useAuth();
    const {user, isForbidden} = useUserContext();
    const {fidelityLevel, fetchFidelityLevel} = useFidelityLevel();
    const drawer = useDrawer();
    const [menuMobileOpen, setMenuMobileOpen] = useState(false);
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [isAssociation, setIsAssociation] = useState<boolean>(false);
    const [isCompany, setIsCompany] = useState<boolean>(false);
    const [userName, setUserName] = useState<string>("");

    const toggleMenuMobile = () => {
        setMenuMobileOpen(!menuMobileOpen);
    };
  
    useEffect(() => {
        let lastScroll = 0;
        let ticking = false;
    
        const handleScroll = () => {
            const bodyHeight = document.body.scrollHeight;
            const windowHeight = window.innerHeight;
    
            const headerFullHeight = !isMobile ? 180 : 70;
            const headerReducedHeight = !isMobile ? 70 : 70;
            const headerDifference = headerFullHeight - headerReducedHeight;
    
            const scrollY = window.scrollY || window.pageYOffset;
            
            // Calculate remaining scroll distance to bottom of page
            const remainingScrollDistance = bodyHeight - windowHeight - scrollY;
    
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    // If we're scrolling down and header is not yet scrolled
                    if (scrollY > headerDifference + 10 && !isScrolled) {
                        // Only contract the header if we have enough remaining scroll space
                        // This prevents flickering near the bottom of the page
                        if (remainingScrollDistance > headerDifference) {
                            setIsScrolled(true);
                        }
                    } 
                    // If we're scrolling up and header is scrolled
                    else if (scrollY <= headerDifference && isScrolled) {
                        // Only expand the header if we're close enough to the top
                        setIsScrolled(false);
                    }
                    
                    ticking = false;
                });
    
                ticking = true;
            }
    
            lastScroll = scrollY;
        };
        
        window.addEventListener('scroll', handleScroll);
    
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isScrolled]);

    useEffect(()=>{
        if (!auth.isLoading) {
            if (auth.isAuthenticated) {
                if(user){
                    // ADMIN (ruoli area amministrazione da configurazione)
                    const adminRoles = adminAreaRoles;
                    // Association
                    const associationRoles = [Roles.Association];
                    // Company
                    const companyRoles = [Roles.Company];

                    const hasRole = (userRoles: string | string[] | undefined | null, rolesToCheck: string[]): boolean => {
                        if (Array.isArray(userRoles)) {
                            return userRoles.some((role: string) => rolesToCheck.includes(role));
                        } else if (typeof userRoles === "string") {
                            return rolesToCheck.includes(userRoles);
                        }
                        return false; // Se userRoles è undefined o null
                    };
                    
                    // ADMIN
                    if (hasRole(user.role, adminRoles)) {
                        setIsAdmin(true);
                    }
                    
                    // Association
                    if (hasRole(user.role, associationRoles)) {
                        setIsAssociation(true);
                    }
                    
                    // Company
                    if (hasRole(user.role, companyRoles)) {
                        setIsCompany(true);
                    }

                    // set del nome utente:
                    if (hasRole(user.role, associationRoles) || hasRole(user.role, companyRoles)) {
                        setUserName(user.companyAssociationInfo?.Name ?? '');
                    } else if (user.isprivate) {
                        setUserName(user.privateInfo?.Name ?? '');
                    } else {
                        setUserName(user.companyAssociationInfo?.Name ?? '');
                    }

                    // fidelity
                    fetchFidelityLevel().finally();
                }
            } else {
                // Clear userName when not authenticated
                setUserName('');
            }
        }
    },[auth.isAuthenticated, auth.isLoading, user, user?.privateInfo?.Name, user?.companyAssociationInfo?.Name])

    const name = user?.userType == 'Private' ? user?.privateInfo?.Name : user?.companyAssociationInfo?.Name ?? '';
    const drawerTitle = (t("Benvenuto") + " " + (name === null ? '' : name) )
    const showBuyAndRedeem = isNationalEntityUser(user);

    const unAuthMenuData: MenuItemType[] | MenuDrawerItemSideBar[] = [
        {nome: t("HOME"), link: "/"},
        /*{nome: t("GLI ASSO"), link: "/asso"},
        {nome: t("LA PIATTAFORMA"), link: "/platform"},
        {nome: t("IL CIRCUITO"), link: "/network"},
        {nome: t("IL MERCATO"), link: "/market"},*/
    ];

    const authMenuData: MenuItemType[] | MenuDrawerItemSideBar[] = [
        {nome: t("HOME"), link: "/home"},
        {nome: t("GESTISCI"), 
            submenu: [
                ...(!isForbidden && showBuyAndRedeem ?[
                    {nome:t("Acquista"), link:"/buy"}
                ] : []),
                {nome:t("Trasferisci"), link:"/withdraw"},
                ...(showBuyAndRedeem && (isAssociation || isCompany)
                ? [
                    // ...(!isForbidden ? [
                    //     { nome: t("Deposita"), link: "/deposit" }
                    // ] : []),
                    { nome: t("Riscatta"), link: "/redeem" }
                ] // Esempio di item condizionale
                : []),
                {nome:t("Archivio transazioni"), link:"/transactions"},
                // ...(!isForbidden ?[
                //     {nome:t("Guadagna"), link:"/fidelity"},
                //     {nome:t("Presenta il circuito"), link:"/present-circuit"},
                // ] : []),
                // {nome:t("Archivio punti fedeltà"), link:"/credits/list"},
                // ...(!isForbidden ?[
                //     {nome:t("Scarica portafogli"), link:`${settings.InfosWebsite.Url}/scarica-portafogli`},
                // ] : []),
            ]
        },
        // ...(!isForbidden ?[
        //     {nome:t("IL CIRCUITO"), link: "/network/list"},
        //     {nome:t("IL MERCATO"), link: "/market/info"},
        // ] : []),
        {nome: "PROFILO", 
            submenu: [
                {nome: t("Informazioni utente"), link:"/user/info"},
                {nome: t("Cambia password"), link:"/user/change-password"},
                // ...(!isForbidden && !isAssociation && !isCompany ? [ // solo privato 
                //     {nome:t("Partecipa al progetto"), link:`${settings.InfosWebsite.Url}/partecipa-al-progetto`},
                //     {nome: t("Informazioni fiscali"), link:`${settings.InfosWebsite.Url}/informazioni-fiscali`}
                // ] : []),
                ...(!isForbidden ? [
                    {nome: t("Chiudi account"), link:"/close-account"},
                ] : []),
            ]
        },
        ...(isAdmin || isAssociation ? // || (isCompany && !isForbidden)
            [
            {nome: t("AMMINISTRAZIONE"), 
                submenu: [
                    ...(isAdmin ? [
                    {nome:t("Gestione utenti"), link:"/admin"},
                    ] : []),

                    ...(isAssociation ? [
                        {nome:t("Gestione utenti"), link:"/admin"},
                    ]:[]),

                    // ...(isCompany
                    // ? [
                    //     {nome:t("Gestisci attività economiche"), link:"/user/economics-activities"},
                    //     {nome:t("Il tuo QR code"), link:"/sell-in-network"},
                    //     {nome: t("Informazioni fiscali"), link:`${settings.InfosWebsite.Url}/informazioni-fiscali`}
                    //     //{nome:t("Vendi online"), link:`${settings.InfosWebsite.Url}/vendi-online/`},
                    //     //{nome:t("Assistenza alle vendite"), link:`${settings.InfosWebsite.Url}/assistenza-alle-vendite`},
                    // ] 
                    // : [])
                ]
            }
        ]: []),
    ];

    return (
        <>
            {!isMobile ? (
                <>
                {/** AUTH */}
                { auth.isAuthenticated ?
                    <HeaderContainer isScrolled={isScrolled} id="header">
                        <ContainerHiddenSubHeader isScrolled={isScrolled}>
                            <Grid container xs={12} spacing={0} justifyContent={{xs:'end'}} alignItems="center" sx={{
                                backgroundColor:WHITE,
                                width: '100%',
                            }}> 
                                {auth.isAuthenticated &&
                                    <Grid item sx={{ margin: '0 25px 0 0', display: 'flex', alignItems: 'center' }}>
                                        <UserFidelityLevelHeader username={userName} gender={fidelityLevel.gender} level={fidelityLevel.level} />
                                    </Grid>    
                                }
                                <GridLogin item margin={'0 5px'}>
                                    <LoginButton />
                                </GridLogin>
                                <GridLogin item margin={'0 5px'}>
                                    <DropDownLanguages />
                                </GridLogin>
                            </Grid>
                        </ContainerHiddenSubHeader>
                        <Grid container spacing={1} flexGrow={1} marginTop={0} sx={{backgroundColor:WHITE, color: BLUE_MAIN}}>
                            <Grid item xs={6} sm={4} sx={{
                                flexGrow:1,
                                display:'flex',
                                justifyContent:'center',
                                alignItems:'center',
                                margin:'0px !important',
                                padding:'0px !important',
                                backgroundColor:WHITE
                            }}>
                                <Logo src={"/"+settings.Details.Logo} 
                                    alt="Plutima Provider" 
                                    isScrolled={isScrolled} 
                                    onClick={()=>{navigate("/")}} 
                                    sx={{cursor: 'pointer'}}
                                />
                            </Grid>
                            <Grid item xs={6} sm={8} sx={{
                                flexGrow:1,
                                display:'flex',
                                justifyContent:'end',
                                alignItems:'center',
                                backgroundColor:WHITE,
                            }}>
                                <Grid container md={12} spacing={1} justifyContent={'end'} marginRight={'15px'}
                                    sx={{ 
                                        borderBottom: { xs: 'none', md: isScrolled ? 'none' : '1px solid green' }, 
                                        paddingBottom: { xs: '0px', md: isScrolled ? '0' : '15px' }, 
                                        marginTop: {md: isScrolled ? '0' : '-40px'}
                                    }}> 
                                       
                                        {/** OVER MD */}
                                        <DynamicMenu items={authMenuData} />

                                        {/** UNDER MD */}
                                        <GridMenuHiddenUp breackpoint='md' item margin={'0 5px'}
                                            sx={{
                                                cursor: 'pointer',
                                            }}
                                        > 
                                            <IconButton onClick={() => {drawer.openOrCloseDrawer(
                                                    authMenuData, 
                                                    drawerTitle!
                                                )}} >
                                                <Avatar sx={{ width:'40px', height:'40px', backgroundColor: WHITE, color: GREEN_MAIN, alignItems:'center' }}>
                                                    <AppsIcon sx={{fontSize:'35px'}} />
                                                </Avatar>
                                            </IconButton>
                                        </ GridMenuHiddenUp>
                                </Grid>
                            </Grid>
                        </Grid>
                    </HeaderContainer>
                    :
                    <HeaderContainerNotScrolled>
                        {/** NOT AUTH */}
                        <ContainerHiddenSubHeader>
                            <Grid container xs={12} spacing={0} justifyContent={{xs:'end'}} sx={{
                                backgroundColor:WHITE,
                            }}> 
                                <GridLogin item margin={'0 5px'}>
                                    <LoginButton />
                                </GridLogin>
                                <GridLogin item margin={'0 5px'}>
                                    <DropDownLanguages />
                                </GridLogin>
                            </Grid>
                        </ContainerHiddenSubHeader>
                    </HeaderContainerNotScrolled>
                }</>

                
            ) : (
                <>
                {/** MOBILE */}
                    <HeaderContainer menuMobileOpen={menuMobileOpen} id="header">
                        <Box
                            sx={{
                                display:"flex",
                                justifyContent:'center',
                                alignItems:'center',
                                height:'65px'
                            }}
                        >
                            <Grid container spacing={1} flexGrow={1} marginTop={0} sx={{backgroundColor:WHITE}}>
                                <Grid item xs={4} sx={{
                                    flexGrow:1,
                                    display:'flex',
                                    justifyContent:'start',
                                    alignItems:'center',
                                    margin:'0px !important',
                                    padding:'0px 0px 0px 35px !important',
                                    backgroundColor:WHITE,
                                    minWidth:'80px'
                                }}>
                                    <Logo src={"/"+settings.Details.Logo} alt="ASSO Provider" isScrolled={isScrolled}/>
                                </Grid>
                                <Grid item xs={8} sx={{
                                    flexGrow:1,
                                    display:'flex',
                                    justifyContent:'end',
                                    alignItems:'center',
                                    margin:'0px !important',
                                    padding:'0px 25px !important',
                                    backgroundColor:WHITE
                                }}>
                                    <Grid container xs={12} spacing={0} justifyContent={'end'}> 
                                        <Grid item margin={'0 5px'}
                                            sx={{
                                                // width:'50px',
                                                display:'flex',
                                                justifyContent:'center',
                                                alignItems:'center',
                                                padding:'0px !important',
                                            }}
                                        > 
                                            <IconButton onClick={() => { !auth.isAuthenticated ?
                                                drawer.openOrCloseDrawer(
                                                    unAuthMenuData, 
                                                    drawerTitle!
                                                ) : 
                                                drawer.openOrCloseDrawer(
                                                    authMenuData, 
                                                    drawerTitle!
                                                )
                                            }} 
                                            >
                                                <Avatar sx={{ width:'40px', height:'40px', backgroundColor: WHITE, color: GREEN_MAIN, alignItems:'center' }}>
                                                    <AppsIcon sx={{fontSize:'35px'}} />
                                                </Avatar>
                                            </IconButton>
                                        </ Grid>
                                        
                                        <Grid item margin={'0 5px'} sx={{
                                                // width:'50px',
                                                display:'flex',
                                                justifyContent:'center',
                                                alignItems:'center',
                                                padding:'0px !important',
                                            }}>
                                            <IconButton
                                                edge="end"
                                                color="inherit"
                                                aria-label="menu"
                                                onClick={()=>toggleMenuMobile()}
                                                >
                                                    <Avatar sx={{ width:'40px', height:'40px', backgroundColor: WHITE, color: GREEN_MAIN, alignItems:'center' }}>
                                                        {menuMobileOpen ? <CloseIcon sx={{fontSize:'35px'}}/> : <MenuIcon sx={{fontSize:'35px'}}/>}
                                                    </Avatar>
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                        {menuMobileOpen && 
                            <Box
                                sx={{
                                    height:'54px',
                                    borderTop: "1px solid "+BLUE_LIGHT_RGB_BORDER,
                                    display:'flex',
                                    justifyContent:'center',
                                    alignItems:'center',
                                }}
                            >
                                <Grid container xs={12} justifyContent={'center'} sx={{
                                    backgroundColor:WHITE,
                                }}> 
                                    {/*!auth.isAuthenticated && 
                                        <GridLogin item margin={'0 5px'}>
                                            <RegisterButton />
                                        </GridLogin>
                                    */}
                                    <GridLogin item margin={'0 5px'}>
                                        <LoginButton />
                                    </GridLogin>
                                    <GridLogin item margin={'0 5px'}>
                                        <DropDownLanguages />
                                    </GridLogin>
                                </Grid>
                            </Box>
                        }
                    </HeaderContainer>
                </>
            )} 
        </>
    )
}

export default Header